import { keyframes } from '@emotion/react';
import { SxProps } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { GivelifyBoxMarginProps } from '../GivelifyBaseProps';
import {
    GivelifyColor,
    GivelifyColorPalette,
    GivelifyColorOrString,
} from '../style/givelifyDefaultTheme';

export const rotation = keyframes({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(359deg)' },
});

const bounce = keyframes({
    '0%': { transform: 'translateY(0px)' },
    '25%': { transform: 'translateY(10px)' },
    '50%': { transform: 'translateY(10px)' },
    '100%': { transform: 'translateY(0px)' },
});

const spinner = keyframes({
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
});

type LoadingColor = 'primary' | 'secondary' | 'alt';

export const getLoadingColor = (theme: Theme, color?: LoadingColor) => {
    switch (color) {
        case 'primary':
            return theme.palette.primary.contrastText;
        case 'secondary':
            return theme.palette.primary.main;
        case 'alt':
            return GivelifyColorPalette.primaryAlt;
        default:
            return undefined;
    }
};

export const LoadingDotsRoot = styled('div')({
    display: 'flex',
});

export const LoadingDot = styled('span', {
    shouldForwardProp: (prop) =>
        prop !== 'loadingColor' && prop !== 'animationDelay',
})<{ loadingColor?: LoadingColor; animationDelay: string }>(
    ({ theme, loadingColor: color, animationDelay }) => ({
        fontSize: 6,
        margin: 2,
        animation: `${bounce} 1000ms ease-in-out infinite`,
        animationDelay,
        transformOrigin: 'center center',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: getLoadingColor(theme, color),
    }),
);

type GivelifyIconStyleProps = {
    iconSize?: string | number;
    iconColor?: GivelifyColorOrString;
    ignoreOwnStyles?: boolean;
    spin?: boolean;
} & GivelifyBoxMarginProps;

export const getGivelifyIconSx = (
    theme: Theme,
    {
        iconSize,
        iconColor,
        ignoreOwnStyles,
        spin,
        margin,
        marginLeft,
        marginTop,
        marginRight,
        marginBottom,
    }: GivelifyIconStyleProps,
): SxProps<Theme> => {
    let resolvedColor: string | undefined;
    if (!ignoreOwnStyles) {
        if (!iconColor) {
            resolvedColor = '#676E77';
        } else {
            const colorKey = iconColor as GivelifyColor;
            resolvedColor =
                GivelifyColorPalette[colorKey] ?? (iconColor as string);
        }
    }

    return {
        ...(!ignoreOwnStyles && {
            marginLeft: marginLeft || margin,
            marginTop: marginTop || margin,
            marginRight: marginRight || margin,
            marginBottom: marginBottom || margin,
            fontSize: iconSize || '24px',
            color: resolvedColor,
        }),
        ...(spin && { animation: `${spinner} 2s linear infinite` }),
    };
};
